import { useEffect, useRef, useState } from "react";
import { useGetIdentity, useNotify } from "ra-core";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Mic, Pause, Play, SendHorizontal, Square, Trash2 } from "lucide-react";

import { getSupabaseClient } from "../providers/supabase/supabase";

// LDF: Sprachnachricht wie bei WhatsApp. Aufnehmen → anhalten/anhören/weiter → senden (Pfeil) oder löschen.
// Senden lädt die Aufnahme in den privaten Speicher; der Mac mini verschriftlicht sie mit Whisper, der Text kommt zurück.

type Phase = "idle" | "preparing" | "recording" | "paused" | "sending" | "waiting";

// Kurzer Piepton: „Ab jetzt sprechen“ (zwei kurze Töne, leise)
const beep = (ctx: AudioContext) =>
  new Promise<void>((resolve) => {
    try {
      const now = ctx.currentTime;
      [0, 0.12].forEach((offset, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = i === 0 ? 880 : 1175;
        gain.gain.setValueAtTime(0.0001, now + offset);
        gain.gain.exponentialRampToValueAtTime(0.25, now + offset + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + offset + 0.09);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now + offset);
        osc.stop(now + offset + 0.1);
      });
    } catch {
      // ohne Ton weiter
    }
    setTimeout(resolve, 260);
  });

const BARS = 48;
const pickMime = () => {
  const candidates = ["audio/webm;codecs=opus", "audio/mp4", "audio/webm", "audio/ogg;codecs=opus"];
  return typeof MediaRecorder !== "undefined" ? candidates.find((m) => MediaRecorder.isTypeSupported?.(m)) ?? "" : "";
};
export const voiceRecordingSupported = () =>
  typeof window !== "undefined" && typeof MediaRecorder !== "undefined" && !!navigator.mediaDevices?.getUserMedia;

/** Aufnahme als Notiz-Anhang (liegt im privaten Speicher, abspielbar in der Notiz) */
export type VoiceAttachment = { src: string; path: string; title: string; type: string };

/** Lebenszeichen des Sprach-Workers auf dem Mac mini (alle 20 s) */
export const useVoiceWorkerOnline = (enabled = true) => {
  const { data } = useQuery({
    queryKey: ["voice-worker-heartbeat"],
    enabled,
    refetchInterval: 30000,
    staleTime: 15000,
    queryFn: async () => {
      const { data } = await getSupabaseClient().from("worker_heartbeats").select("seen_at").eq("name", "voice").maybeSingle();
      return data?.seen_at ? Date.now() - new Date(data.seen_at).getTime() < 90000 : false;
    },
  });
  return data; // undefined = noch unbekannt
};

const clock = (ms: number) => {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

export const VoiceRecorder = ({
  onText,
  hints,
  size = "md",
  onActiveChange,
  onAudio,
  onPendingChange,
  title = "Sprachnachricht aufnehmen",
}: {
  onText: (text: string) => void;
  /** Wenn gesetzt: Aufnahme wird sofort übergeben (z. B. als Notiz-Anhang), der Text kommt im Hintergrund nach */
  onAudio?: (attachment: VoiceAttachment) => void;
  onPendingChange?: (pending: boolean) => void;
  onActiveChange?: (active: boolean) => void;
  title?: string;
  /** Namen/Begriffe, die richtig geschrieben werden sollen (Kontakt, Firma …) */
  hints?: string;
  size?: "md" | "lg";
}) => {
  const [phase, setPhase] = useState<Phase>("idle");
  const [elapsed, setElapsed] = useState(0);
  const [levels, setLevels] = useState<number[]>([]);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const { identity } = useGetIdentity();
  const notify = useNotify();

  const recorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const chunks = useRef<Blob[]>([]);
  const audioCtx = useRef<AudioContext | null>(null);
  const raf = useRef<number | null>(null);
  const clockStart = useRef(0);
  const accumulated = useRef(0);
  const player = useRef<HTMLAudioElement | null>(null);
  const discard = useRef(false);
  const mounted = useRef(true);
  const [pending, setPending] = useState(0);
  const online = useVoiceWorkerOnline();
  const onlineRef = useRef(online);
  onlineRef.current = online;
  useEffect(() => onPendingChange?.(pending > 0), [pending, onPendingChange]);

  useEffect(() => onActiveChange?.(phase !== "idle"), [phase, onActiveChange]);

  const big = size === "lg";
  const btn = big ? "h-14 w-14" : "h-11 w-11";
  const icon = big ? "h-7 w-7" : "h-5 w-5";

  const cleanupStream = () => {
    if (raf.current) cancelAnimationFrame(raf.current);
    raf.current = null;
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
    audioCtx.current?.close().catch(() => {});
    audioCtx.current = null;
  };

  useEffect(
    () => () => {
      mounted.current = false;
      discard.current = true;
      try {
        recorder.current?.stop();
      } catch {
        // schon beendet
      }
      cleanupStream();
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const tick = (analyser: AnalyserNode) => {
    const data = new Uint8Array(analyser.fftSize);
    let last = 0;
    const loop = (t: number) => {
      analyser.getByteTimeDomainData(data);
      let peak = 0;
      for (const v of data) peak = Math.max(peak, Math.abs(v - 128));
      if (t - last > 90) {
        last = t;
        setLevels((prev) => [...prev.slice(-(BARS - 1)), Math.min(1, peak / 60)]);
        setElapsed(accumulated.current + (Date.now() - clockStart.current));
      }
      raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);
  };

  const start = async () => {
    // AudioContext noch im Tipp anlegen – iOS erlaubt Töne sonst nicht
    const ctx = new AudioContext();
    ctx.resume().catch(() => {});
    audioCtx.current = ctx;
    setPhase("preparing");
    try {
      const media = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      stream.current = media;
      const mime = pickMime();
      const rec = new MediaRecorder(media, mime ? { mimeType: mime } : undefined);
      chunks.current = [];
      discard.current = false;
      rec.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
      recorder.current = rec;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      ctx.createMediaStreamSource(media).connect(analyser);
      // Mikrofon bereit → Piepton, danach startet die Aufnahme (der Ton selbst wird nicht mit aufgenommen)
      await beep(ctx);
      if (discard.current) return;
      rec.start(1000);
      if (navigator.vibrate) navigator.vibrate(30);
      accumulated.current = 0;
      clockStart.current = Date.now();
      setLevels([]);
      setElapsed(0);
      setPhase("recording");
      tick(analyser);
    } catch {
      notify("Mikrofon nicht verfügbar – bitte in den Einstellungen für das CRM erlauben.", { type: "warning" });
      cleanupStream();
      setPhase("idle");
    }
  };

  const pause = () => {
    const rec = recorder.current;
    if (!rec || rec.state !== "recording") return;
    rec.pause();
    rec.requestData();
    accumulated.current += Date.now() - clockStart.current;
    if (raf.current) cancelAnimationFrame(raf.current);
    setPhase("paused");
    setTimeout(() => {
      const blob = new Blob(chunks.current, { type: rec.mimeType || "audio/mp4" });
      setPreviewUrl((old) => {
        if (old) URL.revokeObjectURL(old);
        return URL.createObjectURL(blob);
      });
    }, 150);
  };

  const resume = () => {
    const rec = recorder.current;
    if (!rec || rec.state !== "paused") return;
    player.current?.pause();
    setPlaying(false);
    rec.resume();
    clockStart.current = Date.now();
    setPhase("recording");
    if (audioCtx.current) {
      const analyser = audioCtx.current.createAnalyser();
      analyser.fftSize = 512;
      if (stream.current) audioCtx.current.createMediaStreamSource(stream.current).connect(analyser);
      tick(analyser);
    }
  };

  const remove = () => {
    discard.current = true;
    try {
      recorder.current?.stop();
    } catch {
      // schon beendet
    }
    cleanupStream();
    player.current?.pause();
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPlaying(false);
    setLevels([]);
    setElapsed(0);
    setPhase("idle");
  };

  // Wartet auf den Text. null = aufgegeben (Worker offline oder Fenster geschlossen)
  const waitForText = async (id: number): Promise<string | null> => {
    const supabase = getSupabaseClient();
    const startedAt = Date.now();
    for (let i = 0; ; i++) {
      if (!mounted.current) return null;
      const { data } = await supabase.from("voice_transcriptions").select("status,text,error").eq("id", id).maybeSingle();
      if (data?.status === "fertig") return data.text ?? "";
      if (data?.status === "fehler") throw new Error(data.error ?? "Verschriftlichung fehlgeschlagen");
      // Worker offline: nicht endlos warten; laufender Auftrag bekommt großzügig Zeit
      if (onlineRef.current === false && data?.status === "wartet" && Date.now() - startedAt > 8000) return null;
      if (Date.now() - startedAt > 5 * 60 * 1000) return null;
      await new Promise((r) => setTimeout(r, i < 20 ? 1000 : 3000));
    }
  };

  const send = async () => {
    const rec = recorder.current;
    if (!rec) return;
    const durationMs = phase === "recording" ? accumulated.current + (Date.now() - clockStart.current) : accumulated.current;
    setPhase("sending");
    player.current?.pause();
    const blob: Blob = await new Promise((resolve) => {
      rec.onstop = () => resolve(new Blob(chunks.current, { type: rec.mimeType || "audio/mp4" }));
      try {
        rec.stop();
      } catch {
        resolve(new Blob(chunks.current, { type: rec.mimeType || "audio/mp4" }));
      }
    });
    cleanupStream();
    if (discard.current) return;
    if (durationMs < 700 || blob.size < 1000) {
      notify("Aufnahme zu kurz", { type: "warning" });
      return remove();
    }
    let id: number;
    let path: string;
    try {
      const supabase = getSupabaseClient();
      const ext = blob.type.includes("webm") ? "webm" : blob.type.includes("ogg") ? "ogg" : "m4a";
      path = `voice/${identity?.id ?? "x"}/${new Date().toISOString().slice(0, 10)}/${crypto.randomUUID()}.${ext}`;
      const { error: upErr } = await supabase.storage.from("attachments").upload(path, blob, { contentType: blob.type || "audio/mp4" });
      if (upErr) throw upErr;
      const { data, error } = await supabase
        .from("voice_transcriptions")
        .insert({ audio_path: path, mime_type: blob.type, duration_ms: Math.round(durationMs), hints: hints ?? null, sales_id: identity?.id })
        .select("id")
        .single();
      if (error) throw error;
      id = data.id;
    } catch (e) {
      notify(`Aufnahme konnte nicht gespeichert werden: ${e instanceof Error ? e.message : String(e)}`, { type: "error" });
      resetUi();
      return;
    }

    const when = new Date().toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });
    if (onAudio) {
      // Aufnahme sofort an die Notiz hängen – speichern geht auch, bevor der Text da ist
      onAudio({ src: path, path, title: `Sprachnachricht ${clock(durationMs)} · ${when}`, type: blob.type || "audio/mp4" });
      resetUi();
    } else {
      setPhase("waiting");
    }
    if (onlineRef.current === false) {
      notify(
        onAudio
          ? "Mac mini gerade nicht erreichbar – Aufnahme ist gespeichert, der Text wird später automatisch in die Notiz eingetragen."
          : "Mac mini gerade nicht erreichbar – der Text kommt später. Bitte vorerst tippen.",
        { type: "warning" },
      );
    }
    setPending((n) => n + 1);
    try {
      const text = await waitForText(id);
      if (text == null) {
        if (mounted.current && onlineRef.current !== false)
          notify("Die Verschriftlichung dauert ungewöhnlich lange – der Text wird später in die Notiz eingetragen.", { type: "warning" });
      } else if (text.trim()) {
        if (mounted.current) onText(text.trim());
      } else if (mounted.current) {
        notify("In der Aufnahme wurde nichts verstanden", { type: "warning" });
      }
    } catch (e) {
      if (mounted.current) notify(e instanceof Error ? e.message : String(e), { type: "error" });
    } finally {
      if (mounted.current) {
        setPending((n) => n - 1);
        if (!onAudio) resetUi();
      }
    }
  };

  const resetUi = () => {
    setPreviewUrl((old) => {
      if (old) URL.revokeObjectURL(old);
      return null;
    });
    setLevels([]);
    setElapsed(0);
    setPhase("idle");
  };

  const togglePlay = () => {
    const el = player.current;
    if (!el) return;
    if (playing) {
      el.pause();
      setPlaying(false);
    } else {
      el.currentTime = 0;
      el.play().then(() => setPlaying(true)).catch(() => {});
    }
  };

  if (phase === "idle") {
    const status = online === false ? "Mac mini offline – Text kommt später" : online ? "Verschriftlichung bereit" : "";
    return (
      <span className="inline-flex items-center gap-2">
        <button
          type="button"
          onClick={start}
          title={status ? `${title} (${status})` : title}
          aria-label={title}
          className={`relative inline-flex shrink-0 items-center justify-center rounded-full bg-[#25A244] text-white shadow-md hover:bg-[#1f8f3b] active:bg-[#1a7a32] cursor-pointer ${btn}`}
        >
          <Mic className={icon} />
          {online !== undefined && (
            <span
              aria-hidden
              className={`absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-background ${online ? "bg-emerald-400" : "bg-amber-500"}`}
            />
          )}
        </button>
        {pending > 0 && (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" /> Text wird erstellt …
          </span>
        )}
      </span>
    );
  }

  if (phase === "preparing") {
    return (
      <div className="flex min-w-0 flex-1 items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
        <span className="truncate">Mikrofon wird vorbereitet … beim Piepton sprechen</span>
      </div>
    );
  }

  if (phase === "sending" || phase === "waiting") {
    return (
      <div className="flex min-w-0 flex-1 items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
        <span className="truncate">{phase === "sending" ? "Wird hochgeladen …" : "Wird verschriftlicht …"}</span>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-1 items-center gap-2">
      <button type="button" onClick={remove} aria-label="Sprachnachricht löschen" className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted cursor-pointer">
        <Trash2 className="h-5 w-5" />
      </button>

      <div className="flex h-11 min-w-0 flex-1 items-center gap-2 rounded-full border border-border bg-card px-3">
        {phase === "paused" && previewUrl ? (
          <>
            <audio ref={player} src={previewUrl} onEnded={() => setPlaying(false)} className="hidden" />
            <button type="button" onClick={togglePlay} aria-label={playing ? "Anhören stoppen" : "Anhören"} className="shrink-0 cursor-pointer">
              {playing ? <Square className="h-4 w-4 fill-current" /> : <Play className="h-4 w-4 fill-current" />}
            </button>
          </>
        ) : (
          <span className="h-2.5 w-2.5 shrink-0 animate-pulse rounded-full bg-red-600" aria-hidden />
        )}
        <span className="w-10 shrink-0 text-sm tabular-nums">{clock(elapsed)}</span>
        <div className="flex h-6 min-w-0 flex-1 items-center gap-[2px] overflow-hidden" aria-hidden>
          {Array.from({ length: BARS }).map((_, i) => {
            const v = levels[i - (BARS - levels.length)] ?? 0;
            return <span key={i} className="w-[3px] shrink-0 rounded-full bg-muted-foreground/60" style={{ height: `${Math.max(2, v * 24)}px` }} />;
          })}
        </div>
      </div>

      {phase === "recording" ? (
        <button type="button" onClick={pause} aria-label="Aufnahme anhalten" className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted text-red-600 cursor-pointer">
          <Pause className="h-5 w-5 fill-current" />
        </button>
      ) : (
        <button type="button" onClick={resume} aria-label="Weiter aufnehmen" className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted text-red-600 cursor-pointer">
          <Mic className="h-5 w-5" />
        </button>
      )}

      <button
        type="button"
        onClick={send}
        aria-label="Aufnahme beenden und verschriftlichen"
        className={`inline-flex shrink-0 items-center justify-center rounded-full bg-[#25A244] text-white shadow-md active:bg-[#1a7a32] cursor-pointer ${btn}`}
      >
        <SendHorizontal className={icon} />
      </button>
    </div>
  );
};
